import { OverallFileStatus, UpdateStatus } from '~/types/enum';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import { TDownloadableResource } from '~/types/common.type';
import { FileStatus } from '~/store/resource/resource.type';

dayjs.extend(isBetween);
dayjs.extend(utc);

type TGetFileUpdateStatus = {
  downloadedAt?: string | number;
  uploadedAt: string;
};

export const getFileUpdateStatus = ({
  downloadedAt,
  uploadedAt,
}: TGetFileUpdateStatus) => {
  if (uploadedAt && downloadedAt) {
    if (dayjs(uploadedAt).isAfter(dayjs(downloadedAt))) {
      return UpdateStatus.old;
    }
    if (dayjs(uploadedAt).isBefore(dayjs(downloadedAt))) {
      return UpdateStatus.latest;
    }
  } else {
    return UpdateStatus.unknown;
  }
};

export const getDirPath = ({
  key,
  fileName,
}: {
  key: string;
  fileName: string;
}) => key.replace(/\//g, '\\').replace(fileName, '');

export const getOverallFileStatus = (resources: TDownloadableResource[]) => {
  const statuses = resources.map(resource => resource.progress.status);
  const hasCancel = statuses.includes(FileStatus.cancel);
  const isAllFinish = statuses.every(status => status === FileStatus.finish);
  const isNon =
    hasCancel ||
    statuses.every(status => status === FileStatus.non) ||
    statuses.every(
      status => status === FileStatus.finish || status === FileStatus.non,
    );
  const isPause = statuses.includes(FileStatus.paused) && !hasCancel;

  if (isAllFinish) {
    return OverallFileStatus.finish;
  } else if (isNon) {
    return OverallFileStatus.non;
  } else if (isPause) {
    return OverallFileStatus.paused;
  } else {
    return OverallFileStatus.progressing;
  }
};

export const sortObjectByKeyNumber = <T>(
  obj: Record<string, T>,
): Record<string, T> => {
  const sortedKeys = Object.keys(obj).sort((a, b) => {
    const numA = parseInt(a.match(/\d+/)?.[0] || '', 10);
    const numB = parseInt(b.match(/\d+/)?.[0] || '', 10);

    if (isNaN(numA) && isNaN(numB)) return 0;
    if (isNaN(numA)) return 1;
    if (isNaN(numB)) return -1;

    return numA - numB;
  });

  const sortedObj: Record<string, T> = {} as Record<string, T>;
  sortedKeys.forEach(key => {
    sortedObj[key] = obj[key];
  });

  return sortedObj;
};

export const isCurrentWeek = (past: string) => {
  const pastTime = new Date(past).getTime();
  const today = new Date(new Date().toLocaleDateString());
  let day = today.getDay();
  day = day === 0 ? 7 : day;
  const oneDayTime = 24 * 60 * 60 * 1000;
  const mondayTime = today.getTime() - (day - 1) * oneDayTime;
  const nextMondayTime = mondayTime + 7 * oneDayTime;
  return mondayTime <= pastTime && pastTime < nextMondayTime;
};

export const isCurrentMouth = (past: string) => {
  const pastTime = new Date(past).getTime();
  const today = new Date(new Date().toLocaleDateString());
  const year = today.getFullYear();
  const month = today.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  return firstDay.getTime() <= pastTime && pastTime <= lastDay.getTime();
};

export const isBetweenDate = (start: string, end: string) => {
  const current = dayjs().utc();
  return (
    current.isBetween(start, end, 'day') ||
    (current.isSame(start, 'day') && current.isSame(end, 'day'))
  );
};
